/**
 * HotlinkApproval.tsx
 *
 * Standalone approval page that managers reach directly from a link in their
 * notification email.  It deliberately uses raw `fetch()` so no session JWT
 * is sent – the one-time hotlink token in the URL is the sole credential.
 *
 * Route:  /hotlink/approval/:token?action=approve|reject
 */

import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    TextField,
    Typography,
    Alert,
    Chip,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import BlockIcon from '@mui/icons-material/Block';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8787';

interface RequestSummary {
    request_id: number;
    employee_name: string;
    leave_type: string;
    start_date: string;
    end_date: string;
    work_days: number;
    note: string | null;
    status: string;
    created_date: string | null;
}

type PageState = 'loading' | 'summary' | 'confirming' | 'submitting' | 'success' | 'error' | 'already_used';

function formatDate (dateStr: string): string {
    const d = new Date(dateStr + 'T00:00:00Z');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${d.getUTCDate().toString().padStart(2, '0')} ${months[d.getUTCMonth()]}, ${d.getUTCFullYear()}`;
}

export const HotlinkApproval: React.FC = () => {
    const { token } = useParams<{ token: string }>();
    const [searchParams] = useSearchParams();
    const action = (searchParams.get('action') ?? 'approve') as 'approve' | 'reject';

    const [pageState, setPageState] = useState<PageState>('loading');
    const [summary, setSummary] = useState<RequestSummary | null>(null);
    const [reviewComment, setReviewComment] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    // Fetch the request summary using the token
    useEffect(() => {
        if (!token) {
            setErrorMessage('No token provided.');
            setPageState('error');
            return;
        }

        fetch(`${API_URL}/timeoff/hotlink/summary`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        })
            .then(async (res) => {
                if (res.status === 409) {
                    setPageState('already_used');
                    return;
                }
                if (res.status === 410) {
                    setErrorMessage('This link has expired. Hotlink tokens are valid for 72 hours.');
                    setPageState('error');
                    return;
                }
                if (!res.ok) {
                    const body = await res.json().catch(() => ({})) as { message?: string };
                    setErrorMessage(body.message ?? `Unexpected error (${res.status})`);
                    setPageState('error');
                    return;
                }
                const data = await res.json() as RequestSummary;
                setSummary(data);
                setPageState('summary');
            })
            .catch((err: Error) => {
                setErrorMessage(err.message);
                setPageState('error');
            });
    }, [token]);

    const handleConfirm = () => {
        setPageState('confirming');
    };

    const handleSubmit = () => {
        if (!token) return;
        setPageState('submitting');

        const body = action === 'reject' && reviewComment.trim()
            ? JSON.stringify({ review_comment: reviewComment.trim() })
            : undefined;

        fetch(`${API_URL}/timeoff/hotlink/${action}`, {
            method: 'PUT',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body,
        })
            .then(async (res) => {
                if (res.status === 409) {
                    setPageState('already_used');
                    return;
                }
                if (!res.ok) {
                    const errBody = await res.json().catch(() => ({})) as { message?: string };
                    setErrorMessage(errBody.message ?? `Unexpected error (${res.status})`);
                    setPageState('error');
                    return;
                }
                setPageState('success');
            })
            .catch((err: Error) => {
                setErrorMessage(err.message);
                setPageState('error');
            });
    };

    const actionLabel = action === 'approve' ? 'Approve' : 'Reject';
    const actionColor = action === 'approve' ? '#239f84' : '#d9616e';

    return (
        <Box
            sx={{
                minHeight: '100vh',
                background: '#eef1f6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 2,
            }}
        >
            <Card sx={{ maxWidth: 520, width: '100%', boxShadow: 3 }}>
                <Box sx={{ height: 4, background: actionColor }} />
                <CardContent sx={{ p: 4 }}>

                    {/* LOADING */}
                    {pageState === 'loading' && (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <CircularProgress />
                            <Typography sx={{ mt: 2, color: '#6b7280' }}>
                                Loading request details…
                            </Typography>
                        </Box>
                    )}

                    {/* SUMMARY / CONFIRM BUTTON */}
                    {(pageState === 'summary' || pageState === 'confirming') && summary && (
                        <>
                            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                                Time-Off {actionLabel}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#6b7280', mb: 3 }}>
                                You are about to <strong>{actionLabel.toLowerCase()}</strong> the
                                following time-off request.
                            </Typography>

                            <Box
                                sx={{
                                    border: '1px solid #d1d7dc',
                                    borderRadius: 1,
                                    p: 2,
                                    mb: 3,
                                }}
                            >
                                <Typography variant="body2" sx={{ mb: 0.5 }}>
                                    <strong>Employee:</strong> {summary.employee_name}
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 0.5 }}>
                                    <strong>Leave type:</strong>{' '}
                                    <Chip
                                        label={`${summary.leave_type} Leave`}
                                        size="small"
                                        sx={{
                                            background: '#fce6d7',
                                            color: '#F68034',
                                            fontWeight: 600,
                                            height: 20,
                                            fontSize: 12,
                                        }}
                                    />
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 0.5 }}>
                                    <strong>Dates:</strong>{' '}
                                    {summary.start_date === summary.end_date
                                        ? formatDate(summary.start_date)
                                        : `${formatDate(summary.start_date)} – ${formatDate(summary.end_date)}`}
                                    {' '}({summary.work_days} day{summary.work_days !== 1 ? 's' : ''})
                                </Typography>
                                {summary.note && (
                                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                                        <strong>Note:</strong> {summary.note}
                                    </Typography>
                                )}
                            </Box>

                            {/* Reject comment input */}
                            {pageState === 'confirming' && action === 'reject' && (
                                <TextField
                                    label="Rejection reason (optional)"
                                    multiline
                                    rows={3}
                                    fullWidth
                                    value={reviewComment}
                                    onChange={(e) => setReviewComment(e.target.value)}
                                    sx={{ mb: 2 }}
                                />
                            )}

                            {pageState === 'summary' && (
                                <Button
                                    variant="contained"
                                    fullWidth
                                    onClick={handleConfirm}
                                    sx={{
                                        background: actionColor,
                                        '&:hover': { background: action === 'approve' ? '#1a8a71' : '#c0515e' },
                                        fontWeight: 700,
                                        py: 1.2,
                                    }}
                                >
                                    {actionLabel} Request
                                </Button>
                            )}

                            {pageState === 'confirming' && (
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Button
                                        variant="outlined"
                                        fullWidth
                                        onClick={() => setPageState('summary')}
                                    >
                                        Back
                                    </Button>
                                    <Button
                                        variant="contained"
                                        fullWidth
                                        onClick={handleSubmit}
                                        sx={{
                                            background: actionColor,
                                            '&:hover': { background: action === 'approve' ? '#1a8a71' : '#c0515e' },
                                            fontWeight: 700,
                                        }}
                                    >
                                        Confirm {actionLabel}
                                    </Button>
                                </Box>
                            )}
                        </>
                    )}

                    {/* SUBMITTING */}
                    {pageState === 'submitting' && (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <CircularProgress />
                            <Typography sx={{ mt: 2, color: '#6b7280' }}>
                                Submitting…
                            </Typography>
                        </Box>
                    )}

                    {/* SUCCESS */}
                    {pageState === 'success' && (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <CheckCircleOutlineIcon
                                sx={{ fontSize: 64, color: actionColor, mb: 2 }}
                            />
                            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                                Request {actionLabel}d
                            </Typography>
                            <Typography sx={{ color: '#6b7280' }}>
                                {summary?.employee_name
                                    ? `${summary.employee_name}'s`
                                    : 'The'}{' '}
                                time-off request has been{' '}
                                <strong>{action === 'approve' ? 'approved' : 'rejected'}</strong>
                                . The employee will be notified by email.
                            </Typography>
                        </Box>
                    )}

                    {/* ALREADY USED */}
                    {pageState === 'already_used' && (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <BlockIcon sx={{ fontSize: 64, color: '#9ca3af', mb: 2 }} />
                            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                                Link Already Used
                            </Typography>
                            <Typography sx={{ color: '#6b7280' }}>
                                This approval link has already been used. Each link can only
                                be used once.
                            </Typography>
                        </Box>
                    )}

                    {/* ERROR */}
                    {pageState === 'error' && (
                        <Box sx={{ textAlign: 'center', py: 2 }}>
                            <ErrorOutlineIcon sx={{ fontSize: 64, color: '#d9616e', mb: 2 }} />
                            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                                Something went wrong
                            </Typography>
                            <Alert severity="error" sx={{ textAlign: 'left' }}>
                                {errorMessage || 'An unexpected error occurred.'}
                            </Alert>
                        </Box>
                    )}

                </CardContent>
            </Card>
        </Box>
    );
};
